import { canAccessNavRoute, canAccessRoute } from 'navigation/accessors';

import { AuthLayout, CabinetLayout, PublicLayout } from 'router/layouts';
import { RoutesType } from 'router/types';
import { isNotPrivate, isPrivate } from 'router/validators/validators';

import { PATHS } from 'constants/routerPaths';

import { AccountSettingsPage } from 'pages/account';
import { ChangeEmail } from 'pages/change-email';
import { ContinueProgressPage } from 'pages/continue-progress-page';
import { DownloadAppPage, DownloadPageGuard } from 'pages/download-app';
import { FinishWorkoutPage } from 'pages/finish-workout-page';
import { Guides } from 'pages/guides';
import { Login } from 'pages/login';
import { MealPlan } from 'pages/meal-plan';
import { ReturnOffer } from 'pages/return-offer';
import { Shop } from 'pages/shop';
import { StartWorkoutPage } from 'pages/start-workout-page';
import { TemporaryPassword } from 'pages/temporary-password';
import { WorkoutBuilder } from 'pages/workout-builder';
import { WorkoutPage } from 'pages/workout-page';
import { WorkoutPlan } from 'pages/workout-plan';
import { WorkoutPreview } from 'pages/workout-preview';

import { MealPageModule } from '@/pages/meal/MealPageModule';

const PRIVATE_COMMON = {
    redirectPath: PATHS.TEMPORARY_PASSWORD,
    validators: [isPrivate],
};

/** Paid-gated private routes (not in the sidebar registry): auth + paid account. */
const PAID_COMMON = {
    redirectPath: PATHS.MAIN,
    validators: [isPrivate, canAccessRoute({ account: 'paid' })],
};

/**
 * Common config for a private route that is also a sidebar entry — gates on the
 * route's `NAV_ITEMS` access rule so the guard and the registry can't disagree.
 */
const navGated = (path: string) => ({
    redirectPath: PATHS.MAIN,
    validators: [isPrivate, canAccessNavRoute(path)],
});

const PUBLIC_COMMON = {
    redirectPath: PATHS.MAIN,
    validators: [isNotPrivate],
};

export const PRIVATE: Record<string, RoutesType> = {
    ACCOUNT: {
        path: PATHS.ACCOUNT,
        component: AccountSettingsPage,
        titleKey: 'pagesTitles.account',
        layout: CabinetLayout,
        ...PRIVATE_COMMON,
    },
    ACCOUNT_DEFAULT: {
        path: PATHS.ACCOUNT_DEFAULT,
        component: AccountSettingsPage,
        titleKey: 'pagesTitles.account',
        layout: CabinetLayout,
        ...PRIVATE_COMMON,
    },
    CHANGE_EMAIL: {
        path: PATHS.CHANGE_EMAIL,
        component: ChangeEmail,
        titleKey: 'pagesTitles.changeEmail',
        layout: CabinetLayout,
        ...PRIVATE_COMMON,
    },
    MAIN: {
        path: PATHS.MAIN,
        component: DownloadPageGuard,
        titleKey: 'pagesTitles.loginInstruction',
        layout: CabinetLayout,
        ...PRIVATE_COMMON,
    },
    DOWNLOAD_APP: {
        path: PATHS.DOWNLOAD_APP,
        component: DownloadAppPage,
        titleKey: 'pagesTitles.loginInstruction',
        layout: CabinetLayout,
        ...PRIVATE_COMMON,
    },
    FITNESS_GUIDES: {
        path: PATHS.FITNESS_GUIDES,
        component: Guides,
        titleKey: 'pagesTitles.guides',
        layout: CabinetLayout,
        ...PRIVATE_COMMON,
    },
    WORKOUT_BUILDER: {
        path: PATHS.WORKOUT_BUILDER,
        component: WorkoutBuilder,
        titleKey: 'pagesTitles.workoutBuilder',
        layout: CabinetLayout,
        ...navGated(PATHS.WORKOUT_BUILDER),
    },
    MEAL_PLAN: {
        path: PATHS.MEAL_PLAN,
        component: MealPlan,
        titleKey: 'pagesTitles.mealPlan',
        layout: CabinetLayout,
        ...navGated(PATHS.MEAL_PLAN),
    },
    MEAL_PAGE: {
        path: PATHS.MEAL_PAGE,
        component: MealPageModule,
        titleKey: 'pagesTitles.mealPage',
        layout: CabinetLayout,
        ...navGated(PATHS.MEAL_PAGE),
    },
    WORKOUT_PREVIEW: {
        path: PATHS.WORKOUT_PREVIEW,
        component: WorkoutPreview,
        titleKey: 'pagesTitles.workoutPreview',
        layout: CabinetLayout,
        ...PAID_COMMON,
    },
    WORKOUT: {
        path: PATHS.WORKOUT,
        component: WorkoutPage,
        titleKey: 'pagesTitles.workout',
        layout: CabinetLayout,
        ...PAID_COMMON,
    },
    WORKOUT_PLAN: {
        path: PATHS.WORKOUT_PLAN,
        component: WorkoutPlan,
        titleKey: 'pagesTitles.workoutPlan',
        layout: CabinetLayout,
        ...navGated(PATHS.WORKOUT_PLAN),
    },
    STORE_PAGE: {
        path: PATHS.STORE_PAGE,
        component: Shop,
        titleKey: '',
        layout: PublicLayout,
        ...PRIVATE_COMMON,
    },
    RETURN_OFFER: {
        path: PATHS.RETURN_OFFER,
        component: ReturnOffer,
        titleKey: '',
        layout: CabinetLayout,
        ...PRIVATE_COMMON,
    },
    START_WORKOUT_PAGE: {
        path: PATHS.START_WORKOUT_PAGE,
        component: StartWorkoutPage,
        titleKey: 'pagesTitles.installApp',
        layout: CabinetLayout,
        ...PRIVATE_COMMON,
    },
    FINISH_WORKOUT_PAGE: {
        path: PATHS.FINISH_WORKOUT_PAGE,
        component: FinishWorkoutPage,
        titleKey: 'pagesTitles.installApp',
        layout: CabinetLayout,
        ...PRIVATE_COMMON,
    },
    CONTINUE_PROGRESS_PAGE: {
        path: PATHS.CONTINUE_PROGRESS_PAGE,
        component: ContinueProgressPage,
        titleKey: 'pagesTitles.installApp',
        layout: CabinetLayout,
        ...PRIVATE_COMMON,
    },
};

export const PUBLIC: Record<string, RoutesType> = {
    LOGIN: {
        path: PATHS.LOGIN,
        component: Login,
        titleKey: 'pagesTitles.login',
        layout: AuthLayout,
        ...PUBLIC_COMMON,
    },
    TEMPORARY_PASSWORD: {
        path: PATHS.TEMPORARY_PASSWORD,
        component: TemporaryPassword,
        titleKey: 'pagesTitles.login',
        layout: AuthLayout,
        ...PUBLIC_COMMON,
    },
    DOWNLOAD_APP_INSTRUCTION: {
        path: PATHS.DOWNLOAD_APP_INSTRUCTION,
        component: DownloadAppPage,
        titleKey: 'pagesTitles.installApp',
        layout: PublicLayout,
        ...PUBLIC_COMMON,
    },
};

export const routes = Object.values({ ...PRIVATE, ...PUBLIC });

export default {
    PRIVATE,
    PUBLIC,
};
